import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material'
import { IconX } from '@tabler/icons-react'
import { useMemo, useState } from 'react'
import { filter, groupBy, mapValues, pipe, sumBy, times, uniqueBy } from 'remeda'
import { v4 } from 'uuid'
import { useImport } from '~/features/import-export/useImport'
import { useImportInventory } from '~/features/inventory/hooks/useImportInventory'
import { useSendDataAsAttachment } from '~/features/hooks/useSendDataAsAttachment'
import { useUploadFileToBucket } from '~/features/hooks/useUploadFileToBucket'
import {
  inventoryArraySchema,
  InventoryArraySchemaT,
  inventorySample,
} from '~/features/models/models-list/validators/inventoryValidators'
import * as XLSX from 'xlsx'
import { useBatchUpdateInventory } from '~/features/inventory/hooks/useBatchUpdateInventory'
import { useGetAllModels } from '~/features/models/models-list/hooks/useGetAllModels'
import { InventoryV2 } from '~/supabase/types'

const getGroupKey = (row: InventoryArraySchemaT[number]) =>
  `${row.stock_code_v2}-${row.size}-${row.location}`

const combineAllDuplicates = (data: InventoryArraySchemaT) => {
  const grouped = mapValues(groupBy(data, getGroupKey), (rows) => {
    return sumBy(rows, (row) => row.quantity)
  })
  const uniqueData = uniqueBy(data, getGroupKey)
  const result = uniqueData.flatMap((row) => {
    const quantity = grouped[getGroupKey(row)]
    return quantity !== undefined
      ? {
          ...row,
          quantity,
        }
      : []
  })

  return result
}

export const ImportInventory = () => {
  const [search, setSearch] = useState('')
  const { ImportButton, excelData, reset } = useImport({
    schema: inventoryArraySchema,
    sample: inventorySample,
  })
  const { data: models } = useGetAllModels()

  const modelMap = useMemo(() => {
    if (!models) return new Map()
    return new Map(models.map((model) => [model.stock_code, model]))
  }, [models])

  const missingModelsMap = useMemo(() => {
    if (!excelData) return new Map()
    return new Map(
      excelData
        .filter((row) => !modelMap.has(row.stock_code_v2))
        .map((row) => [row.stock_code_v2, row])
    )
  }, [excelData, modelMap])

  const hasMissingModel = missingModelsMap.size > 0

  const normalizedExcelData = useMemo(() => {
    if (!excelData) return []
    return combineAllDuplicates(excelData)
  }, [excelData])

  const inventoryToImport = useMemo(() => {
    return pipe(normalizedExcelData, (data) =>
      data.flatMap((row) =>
        times(
          row.quantity,
          () =>
            ({
              stock_code_v2: row.stock_code_v2,
              location: row.location,
              status: row.status,
              sales: null,
              import_file_name: null,
              cost: row.cost,
              size: row.size,
              stock_code: null,
              inventory_item_id: null,
              product_variant_id: null,
            }) as const satisfies Omit<InventoryV2, 'id' | 'created_at'>
        )
      )
    )
  }, [normalizedExcelData])

  const handleClose = () => {
    reset()
  }

  const { mutateAsync: importInventory, isPending: isImporting } = useImportInventory()
  const { mutate: batchUpdateInventory, isPending: isBatchUpdating } = useBatchUpdateInventory()
  const { mutateAsync: sendDataAsAttachment, isPending: isSendingEmail } = useSendDataAsAttachment()
  const { mutateAsync: uploadFileToBucket, isPending: isUploading } = useUploadFileToBucket()

  // Convert Excel data to base64
  const convertExcelToBase64 = () => {
    if (!excelData || excelData.length === 0) return

    // Create worksheet from data
    const worksheet = XLSX.utils.json_to_sheet(excelData)

    // Create workbook and add the worksheet
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Inventory Data')

    // Convert workbook to binary string
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'binary' })

    // Convert binary string to base64
    const base64Data = btoa(
      String.fromCharCode.apply(
        null,
        Array.from(new Uint8Array(excelBuffer.split('').map((c: string) => c.charCodeAt(0))))
      )
    )

    return base64Data
  }

  const handleUploadAndSend = async () => {
    if (!excelData || excelData.length === 0) return

    const importedInventory = await importInventory(inventoryToImport)

    const now = new Date()
    const isoDate = now.toISOString()
    const filename = `import_inventory_${isoDate}.xlsx`

    // First, convert Excel data to base64 if not already done
    const base64Data = convertExcelToBase64()
    if (!base64Data) return

    const uploadedFileName = await uploadFileToBucket({
      filePath: filename,
      fileContent: base64Data,
      options: {
        bucketName: 'imports',
        contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    })

    await sendDataAsAttachment({
      data: excelData,
      options: {
        subject: 'Inventory Import Data',
        text: 'Please find the attached inventory import data.',
        filename,
      },
    })
    const inventoryWithFile = importedInventory.map((data) => ({
      ...data,
      import_file_name: uploadedFileName,
    }))

    batchUpdateInventory(inventoryWithFile, { onSuccess: handleClose })
  }

  const sortedList = useMemo(() => {
    const searched = search
      ? filter(normalizedExcelData, (row) =>
          row.stock_code_v2.toLowerCase().includes(search.toLowerCase())
        )
      : normalizedExcelData

    return searched.sort((a, b) => {
      // If one is in missingModelsMap and the other isn't, put the missing one first
      const aIsMissing = missingModelsMap.has(a.stock_code_v2)
      const bIsMissing = missingModelsMap.has(b.stock_code_v2)
      if (aIsMissing && !bIsMissing) return -1
      if (!aIsMissing && bIsMissing) return 1
      // If both are missing or both exist, sort by stock_code
      return a.stock_code_v2.localeCompare(b.stock_code_v2)
    })
  }, [normalizedExcelData, missingModelsMap, search])

  const isLoading = isUploading || isSendingEmail || isImporting || isBatchUpdating

  return (
    <Box>
      <Dialog
        open={inventoryToImport.length > 0}
        PaperProps={{ sx: { minWidth: 400 } }}
        onClose={handleClose}
      >
        <Stack p={2} gap={2} overflow="hidden">
          <Stack direction="row" justifyContent="space-between" alignItems="cetner">
            <Typography variant="h6">Import list</Typography>
            <IconButton onClick={handleClose}>
              <SvgIcon>
                <IconX />
              </SvgIcon>
            </IconButton>
          </Stack>
          {hasMissingModel ? (
            <Alert severity="warning" variant="outlined">
              You must create these models first!
            </Alert>
          ) : null}
          <TextField
            placeholder="Search by code"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            size="small"
          />
          <Box overflow="auto">
            {(sortedList ?? []).map((row, i) => (
              <Stack
                key={v4()}
                direction="row"
                justifyContent="space-between"
                px={2}
                py={1}
                my={1}
                component={Card}
                elevation={0}
                variant="outlined"
                sx={{
                  '&:before': {
                    content: `"${i + 1}"`,
                    color: (t) => t.palette.text.disabled,
                    fontSize: (t) => t.typography.caption.fontSize,
                    mr: 1,
                  },
                }}
              >
                <Typography
                  flex={0.5}
                  color={missingModelsMap.has(row.stock_code_v2) ? 'warning' : 'textPrimary'}
                >
                  {row.stock_code_v2}
                </Typography>
                <Typography flex={0.1}>{row.size}</Typography>
                <Typography flex={0.3}>{row.location}</Typography>
                <Typography flex={0.1}>{row.quantity}</Typography>
              </Stack>
            ))}
          </Box>
          <Stack direction="row" justifyContent="space-between">
            <Typography>Total quantity {inventoryToImport.length}</Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={handleUploadAndSend}
                endIcon={isLoading ? <CircularProgress size={14} /> : null}
                disabled={isLoading || hasMissingModel}
              >
                Import
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Dialog>
      <ImportButton label="Import" />
    </Box>
  )
}
